import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth }   from '../contexts/AuthContext';
import { useLang }   from '../contexts/LangContext';
import ThemeSwitcher from '../components/ThemeSwitcher';
import LangSwitcher  from '../components/LangSwitcher';
import styles from './AuthPage.module.css';

export default function SignupPage() {
  const { signup, loginWithProvider }  = useAuth();
  const { t }       = useLang();
  const navigate    = useNavigate();
  const [form, setForm]     = useState({ display_name: '', email: '', password: '', confirm: '' });
  const [error, setError]   = useState('');
  const [loading, setLoading] = useState(false);

  function update(field) { return e => setForm(f => ({ ...f, [field]: e.target.value })); }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!form.display_name || !form.email || !form.password) { setError(t('auth.requiredField')); return; }
    if (form.password.length < 8) { setError(t('auth.weakPassword')); return; }
    if (form.password !== form.confirm) { setError(t('auth.passwordMismatch')); return; }
    setLoading(true);
    try {
      await signup(form.email, form.password, form.display_name);
      navigate('/app', { replace: true });
    } catch (err) {
      setError(err.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  }

  async function handleSocialLogin(provider) {
    setError('');
    setLoading(true);
    try {
      await loginWithProvider(provider);
      navigate('/app', { replace: true });
    } catch (err) {
      setError(err.message || 'Social login failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.controls}>
        <LangSwitcher />
        <ThemeSwitcher />
      </div>

      {/* Left brand panel */}
      <div className={styles.brand}>
        <div className={styles.brandLogo}>
          <div className={styles.brandLogoIcon} style={{ background: 'transparent' }}>
            <img src="/logo2.png" alt="Qeduvo" style={{ width: '100%', height: '100%', borderRadius: 'inherit', objectFit: 'cover' }} />
          </div>
          <span className={styles.brandLogoText}>Qeduvo</span>
        </div>
        <h1 className={styles.brandHeading}>
          Start your<br /><span>journey today.</span>
        </h1>
        <p className={styles.brandSub}>
          Create your free account and unlock access to 200+ courses, a thriving community, and tools to keep you on track.
        </p>
        <div className={styles.brandFeatures}>
          {['Free account, no credit card needed', 'Earn XP & level up daily', 'Compete on live leaderboards', 'Community support & mentors'].map(f => (
            <div key={f} className={styles.brandFeature}>
              <div className={styles.brandFeatureDot} />
              {f}
            </div>
          ))}
        </div>
      </div>

      {/* Right form panel */}
      <div className={styles.formPanel}>
        <div className={styles.card}>
          <div className={styles.cardInner}>
            <div className={styles.logo}>
              <div className={styles.logoIcon} style={{ background: 'transparent' }}>
                <img src="/logo2.png" alt="Qeduvo" style={{ width: '100%', height: '100%', borderRadius: 'inherit', objectFit: 'cover' }} />
              </div>
              <span className={styles.logoText}>Qeduvo</span>
            </div>
            <h2 className={styles.title}>{t('auth.signup')}</h2>
            <p className={styles.sub}>Join thousands of learners today.</p>

            <form onSubmit={handleSubmit} className={styles.form}>
              {error && <div className={styles.error}>{error}</div>}
              <div className="form-group">
                <label className="form-label">{t('auth.displayName')}</label>
                <input id="input-name" type="text" className="form-input" value={form.display_name} onChange={update('display_name')} placeholder="Your name" autoFocus />
              </div>
              <div className="form-group">
                <label className="form-label">{t('auth.email')}</label>
                <input id="input-email" type="email" className="form-input" value={form.email} onChange={update('email')} placeholder="you@example.com" />
              </div>
              <div className="form-group">
                <label className="form-label">{t('auth.password')}</label>
                <input id="input-password" type="password" className="form-input" value={form.password} onChange={update('password')} placeholder="Min. 8 characters" />
              </div>
              <div className="form-group">
                <label className="form-label">{t('auth.confirmPassword')}</label>
                <input id="input-confirm" type="password" className="form-input" value={form.confirm} onChange={update('confirm')} placeholder="••••••••" />
              </div>
              <button id="btn-signup" type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: 6 }} disabled={loading}>
                {loading ? t('common.loading') : t('auth.signupButton')} →
              </button>
            </form>

            <div className={styles.divider}>{t('auth.or')}</div>

            <div className={styles.socialButtons}>
              <button
                type="button"
                className={`${styles.socialButton} ${styles.googleBtn}`}
                onClick={() => handleSocialLogin('google')}
                disabled={loading}
              >
                <img src="https://upload.wikimedia.org/wikipedia/commons/c/c1/Google_%22G%22_logo.svg" alt="Google" className={styles.socialIcon} />
                <span>{t('auth.continueWithGoogle')}</span>
              </button>
              <button
                type="button"
                className={`${styles.socialButton} ${styles.lineBtn}`}
                onClick={() => handleSocialLogin('line')}
                disabled={loading}
              >
                <img src="https://upload.wikimedia.org/wikipedia/commons/4/41/LINE_logo.svg" alt="LINE" className={styles.socialIcon} />
                <span>{t('auth.continueWithLine')}</span>
              </button>
            </div>

            <div className={styles.footer}>
              {t('auth.hasAccount')}{' '}
              <Link to="/login" className={styles.link}>{t('auth.signin')}</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}